<template>
  <ErrorBoundary>
    <div class="flex justify-between items-center text-sm">
      <div>
        <div>
          <label class="font-medium">
            {{
              isBoxEvent
                ? t('product.show.boxDetails.buyBoxQuantity')
                : t('product.show.checkout.quantity')
            }}
          </label>
        </div>
        <div
          class="block text-xs font-normal text-gray-700 mr-2"
          :class="{
            'text-red':
              triedToGoBeyondMax ||
              (triedToGoBelowMin && !props.isGroupPricing),
          }"
        >
          {{ subtitle }}
        </div>
      </div>
      <NumberControlButtons
        v-model="quantity"
        :min="minSelectableTickets"
        :disable-minus="quantity <= minSelectableTickets"
        :disable-plus="quantity >= maxQty && isBoxEvent"
        :is-dates-loaded="isDatesLoaded"
        @min-reached="handleMinReached"
      />
    </div>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
import type { EventDate } from '~/composables/types/event'
const { t } = useI18n()

const props = defineProps<{
  selectedDate: EventDate | undefined
  isBookingAsPrivate: boolean
  isBoxEvent: boolean
  isDatesLoaded: boolean
  isGroupPricing?: boolean
  minTickets?: number
  maxTickets?: number
}>()

const isInitialized = ref(props.isBookingAsPrivate)

const emit = defineEmits<{
  (e: 'triedToGoBeyondMax', value: boolean): void
  (e: 'triedToGoBelowMin', value: boolean): void
}>()

const { availableTickets, minQty, maxQty } = useTicketAvailability({
  selectedDate: computed(() => props.selectedDate),
  isBookingAsPrivate: computed(() => props.isBookingAsPrivate),
  minTickets: computed(() => props.minTickets),
  maxTickets: computed(() => props.maxTickets),
})

const minSelectableTickets = 1

const quantity = defineModel<number>({ default: 1 })

const { triedToGoBelowMin, triedToGoBeyondMax, setErrorState } =
  useQuantityValidation(quantity, minQty, maxQty, {
    onBelowMin: () => {
      emit('triedToGoBelowMin', true)
    },
    onBeyondMax: () => {
      emit('triedToGoBeyondMax', true)
    },
  })

const subtitle = computed(() => {
  if (triedToGoBelowMin.value && !props.isGroupPricing) {
    return t('product.show.checkout.triedToAddBelowMinTickets', {
      minQty: minQty.value,
    })
  }

  if (triedToGoBeyondMax.value) {
    if (props.isBoxEvent) {
      if (availableTickets.value === 0) {
        return t('product.show.checkout.triedToAddKitsSoldout')
      } else {
        return t('product.show.checkout.triedToAddBeyondMaxAvailableKits', {
          n: maxQty.value,
        })
      }
    }

    return t('product.show.checkout.triedToAddBeyondMaxTickets')
  }

  if (props.isBookingAsPrivate) {
    return t('product.show.checkout.buyBoxPrivateQuantityHelperText')
  }
  if (availableTickets.value === 0 && props.isBoxEvent) {
    return t('product.show.checkout.triedToAddKitsSoldout')
  }
  return props.isBoxEvent
    ? t('product.show.boxDetails.buyBoxQuantityHelperText')
    : t('product.show.checkout.privateQuantityNoTicketsSelected')
})

const handleMinReached = () => {
  if (!isInitialized.value) {
    isInitialized.value = true
    return
  }
  setErrorState('below-min')
}
// Watch for constraint violations is handled by useQuantityValidation composable
</script>
